/**
 * ================================================================================================
 *  THE AREAS, AND WHICH SCREENS BELONG TO EACH. **Section 1, 2026-10-27.**
 * ================================================================================================
 *
 * Owner, verbatim:
 *
 *   > *"כרגע המבנה משתנה בין מסכים: לשוניות נעלמות, כותרות ופעולות מחליפות מיקום, והמשתמש צריך
 *   > ללמוד מחדש איך לנווט. נדרשת מעטפת אחידה: ... לכל אזור שנפתח מהתפריט יהיה סרגל לשוניות קבוע,
 *   > המציג את תתי־הנושאים שלו. הסרגל יהיה באותו מיקום אנכי ובאותו גובה בכל המסכים. החלפת לשונית
 *   > תשנה רק את התוכן שמתחתיו. הלשוניות לא ייעלמו בעת פתיחת משתמש, חברה או מסך עריכה. ... יש
 *   > להשתמש ברכיב מעטפת משותף, ולא למקם את הסרגל מחדש בכל מסך."*
 *
 *   > *"לדוגמה, באזור ניהול המשתמשים הלשוניות „משתמשים”, „תפקידים והרשאות” ו„הגדרת תפקידים”
 *   > נשארות במקומן; רק אזור העבודה מתחלף."*
 *
 * This file is the WHAT. `AreaBar.tsx` is the WHERE, and it is one component in the shell — the
 * *"רכיב מעטפת משותף"* — so no screen positions a strip of its own and none can drift.
 *
 * ── A TAB OWNS PATTERNS, AND THAT IS THE WHOLE MECHANISM BEHIND "הלשוניות לא ייעלמו" ───────────
 *
 * A tab names its own address AND every address underneath it. `/tenant/users/invite` is listed by
 * the משתמשים tab, so opening the invitation wizard keeps the area's strip with משתמשים marked
 * current, instead of landing on a screen that has lost its navigation. The same for the company
 * card, the role editor, a customer's card on the platform console and the price-book editor.
 *
 * It is a declared list and not `startsWith`, deliberately. `/tenant/roles/authoring` is BENEATH
 * `/tenant/roles` and is a tab of its own — the Owner named it as one — so a prefix rule would put
 * it in two places at once and silently pick one. `routes.ts` already learned this lesson about
 * `/security` being a prefix of the two MFA addresses: *"membership is decided by an EXACT pattern
 * match and never by `startsWith`"*.
 *
 * ── WHO MAY SEE A TAB IS THE ROUTE'S OWN ANSWER, NOT A SECOND OPINION ──────────────────────────
 *
 * Each tab names the OPERATION a reader of that screen must be able to call, and
 * `registry/routes.baseline.json` says which capability that operation requires. `areas.test.ts`
 * asserts the two agree, row by row. So the strip cannot show a door the gate would slam, and it
 * cannot hide one the gate would open — and when a route's capability changes, the test fails here
 * rather than a customer finding a refused tab.
 *
 * `capability` is written out rather than imported from the registry at runtime because the browser
 * bundle does not read that file; the same arrangement `destinations.ts` already uses, with a
 * stricter check over it.
 *
 * ── EVERY ADDRESS IN THE PRODUCT IS ACCOUNTED FOR, OR THE BUILD FAILS ──────────────────────────
 *
 * `NO_AREA` is the other half of the table: an address that is deliberately NOT in an area, each
 * with the reason written beside it. `areas.test.ts` requires that every pattern in `routePatterns`
 * appears exactly once across the areas and `NO_AREA` together. A screen added next month is
 * therefore placed on purpose or it does not ship — which is what this repository means by a
 * derived control rather than a hand list, applied to the one thing here that genuinely is a
 * product decision.
 */

import { routePatterns } from '../routes';
import type { TextKey } from '../i18n/catalogue';
import type { Surface } from './destinations';

export interface AreaTab {
  /** Stable, ASCII. Used by the tests and by `aria-controls`; never shown to anybody. */
  readonly id: string;
  readonly label: TextKey;
  /** Where the tab goes. Must be the first of its own `patterns`. */
  readonly href: string;
  /**
   * The operation whose capability decides who may see this tab — the screen's own primary read,
   * or for an authoring screen the write it exists to perform.
   */
  readonly operation: string;
  /** What that operation requires, per `registry/routes.baseline.json`. `null` ⇒ open to anyone. */
  readonly capability: string | null;
  /** This tab's own address and every address beneath it that must keep the strip. */
  readonly patterns: readonly string[];
}

export interface Area {
  readonly id: string;
  /** Names the strip for a screen reader. Not drawn: the tabs themselves are the visible labels. */
  readonly label: TextKey;
  readonly surface: Surface;
  readonly tabs: readonly AreaTab[];
}

/**
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *  ניהול הארגון — the customer's administration area.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * The rail's `הגדרות` group opens four of these. The other four — חברות, הגדרת תפקידים, חיבורים
 * and מנוי ומכסות — are screens that exist and work today and were reachable only by a link from
 * one particular screen, which is precisely the *"המשתמש צריך ללמוד מחדש איך לנווט"* the Owner
 * described. Nothing here is new, nothing is relaxed: each tab carries the capability its own route
 * already requires, and `areas.test.ts` binds every rail member of the group to a tab in this list
 * so the two views of one area cannot disagree.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE AREA THAT USED TO BE HERE, AND WHY IT IS NOT — 2026-10-28
 * ─────────────────────────────────────────────────────────────────────────────────
 *
 * `ORGANISATION` carried EIGHT tabs — the organisation, companies, users, roles, role authoring,
 * connections, security and the subscription — and drew them on every one of those screens. It was
 * one bar listing the whole settings area from inside each of its parts.
 *
 * The Owner, with the reference open and a screenshot of that bar:
 *
 *     *"מה ההבדל בין תפקידים והרשאות להגדרת תפקידים ... הרבה מהרשימה שיש בטאבים
 *     צריך להיות בקוביות ברגע שאתה לוחץ על הגדרות."*
 *
 * Both halves are right. Two of the eight tabs were `תפקידים והרשאות` and `הגדרת תפקידים`,
 * which are two different jobs wearing almost the same name; and the list as a whole is the menu
 * that now lives on the settings landing as cards.
 *
 * ── WHAT REPLACES IT, FROM THE REFERENCE ───────────────────────────────────────────────────────
 *
 * In the reference, a settings category's tab strip carries THAT CATEGORY'S OWN TOPICS. So an area
 * exists here only where a category HAS more than one topic:
 *
 *   תפקידים והרשאות   — what a role contains, and who holds it. The two screens whose names
 *                       confused him are now the two tabs of one subject, named for the question
 *                       each one answers rather than for the word they share.
 *   חברות              — the register, and who may open which company.
 *
 * Every other category is ONE screen, so it has no strip at all: `AreaBar` still renders, because
 * it is where חזרה lives at its fixed position and height, and חזרה goes back to הגדרות. A bar
 * with one tab on it is a tab that decides nothing.
 */
const ROLES_AND_PERMISSIONS: Area = {
  id: 'rolesAndPermissions',
  label: 'settings.cat.roles',
  surface: 'TENANT',
  tabs: [
    {
      /*
       * WHAT A ROLE CONTAINS. Authoring: the capability sets themselves. It is named for the
       * question rather than for the word -- הגדרת תפקידים beside תפקידים והרשאות told a
       * reader nothing about which one to open.
       */
      id: 'rolesDefinition',
      label: 'settings.tab.roleDefinition',
      href: routePatterns.tenantRoleAuthoring,
      operation: 'tenant.role.read',
      capability: 'tenant.role.manage',
      patterns: [routePatterns.tenantRoleAuthoring],
    },
    {
      /* WHO HOLDS WHICH ROLE. Assignment, which is a capability grant to a PERSON. */
      id: 'rolesHolders',
      label: 'settings.tab.roleHolders',
      href: routePatterns.tenantRoles,
      operation: 'tenant.member.read',
      capability: 'tenant.user.manage',
      patterns: [routePatterns.tenantRoles],
    },
  ],
};

const COMPANIES: Area = {
  id: 'companies',
  label: 'settings.cat.companies',
  surface: 'TENANT',
  tabs: [
    {
      id: 'companiesRegister',
      label: 'settings.tab.companyRegister',
      href: routePatterns.tenantCompanies,
      operation: 'companies.register.read',
      capability: 'companies.company.manage',
      /* The register, the onboarding wizard and one company's card are one subject. */
      patterns: [
        routePatterns.tenantCompanies,
        routePatterns.tenantCompanyOnboarding,
        routePatterns.tenantCompany,
      ],
    },
    {
      /*
       * WHO MAY OPEN WHICH COMPANY. It lives with the COMPANIES and not with the people, because
       * it is granted per company and the Owner reads it that way.
       */
      id: 'companiesAccess',
      label: 'settings.tab.companyAccess',
      href: routePatterns.tenantCompanyAccess,
      operation: 'tenant.grant.read',
      capability: 'companies.assignment.manage',
      patterns: [routePatterns.tenantCompanyAccess],
    },
  ],
};

/**
 * לקוחות ומנויים — the platform console's customer area.
 *
 * Every screen about ONE customer is beneath the לקוחות tab: the card, the provisioning wizard,
 * the resumed wizard, the subscription and the commercial decisions. Six addresses, one subject,
 * one strip that stays put while an operator walks through them.
 */
const PLATFORM_CUSTOMERS: Area = {
  id: 'platformCustomers',
  label: 'nav.customers',
  surface: 'PLATFORM',
  tabs: [
    {
      id: 'platformCustomersList',
      label: 'nav.customers',
      href: routePatterns.platformTenants,
      operation: 'platform.tenant.read',
      capability: 'platform.tenant.read',
      patterns: [
        routePatterns.platformTenants,
        routePatterns.platformTenantProvisioning,
        routePatterns.platformTenant,
        routePatterns.platformTenantProvisioningResume,
        routePatterns.platformTenantSubscription,
        routePatterns.platformTenantCommercial,
      ],
    },
    {
      id: 'platformSubscriptions',
      label: 'nav.subscriptions',
      href: routePatterns.platformBilling,
      operation: 'platform.metrics.read',
      capability: 'platform.subscription.history.read',
      patterns: [routePatterns.platformBilling],
    },
  ],
};

/**
 * מרכז חיוב — THE FOUR SCREENS OF THE MONEY, AS ONE GROUP. **2026-11-03.**
 *
 * The Owner: *"הפוך את „מרכז חיוב" בסרגל הימני לקבוצה נפתחת עם כניסה ישירה ל: סקירת חיובים,
 * ביצוע חיובים, גבייה וחובות, בקשות שינוי. הצג כל פריט בהתאם להרשאות, סמן את המסך הפעיל ופתח את
 * הקבוצה כשנמצאים באחד ממסכיה. השאר גם את הקישורים מתוך מרכז החיוב."*
 *
 * ── IT WAS FOUR AREA-LESS SCREENS UNTIL TODAY, AND THAT WAS RIGHT WHEN IT WAS ONE ─────────────
 *
 * `NO_AREA` carried all four with the reason this file states: *"one tab is not a strip, it is a
 * label pretending to be navigation"*. That is true of a single screen. It stopped being true
 * when the collections console, the change-request queue and the billing run joined the charges
 * screen: four related topics are what an area IS, and leaving them loose meant the only way to
 * the collections screen was through another screen's footer.
 *
 * ── EACH ENTRY CARRIES ITS OWN CAPABILITY, AND THEY ARE NOT THE SAME ──────────────────────────
 *
 * Three read under `platform.billing.history.read` -- 08 §12B.3 row 10, held by the owner, the
 * operator and the auditor. The fourth, running the billing, is `platform.billing.run.execute`,
 * which is a DIFFERENT authority and is held by fewer people: `visibleTabs` filters on exactly
 * this, so an auditor sees three entries and not a fourth that would refuse them.
 *
 * `patterns` IS WHAT MARKS THE CURRENT ONE and opens the group on arrival -- the shell reads it,
 * so neither behaviour is a second implementation here.
 */
const PLATFORM_BILLING_CENTRE: Area = {
  id: 'platformBillingCentre',
  label: 'nav.billing',
  surface: 'PLATFORM',
  tabs: [
    {
      id: 'billingOverview',
      label: 'nav.billingOverview',
      href: routePatterns.platformCharges,
      operation: 'platform.clearing.read',
      capability: 'platform.billing.history.read',
      patterns: [routePatterns.platformCharges],
    },
    {
      /*
       * THE APPROVAL SCREEN, AND AFTER 2026-11-03 THAT IS ALL IT IS. The Owner: *"אין לבצע
       * חיובים כספיים אוטומטיים... אני אבחר לקוחות, אבדוק ואאשר במפורש את ביצוע החיובים."* The
       * scheduled run prepares the recommendation table; this is where a person approves it.
       */
      id: 'billingRun',
      label: 'nav.billingRun',
      href: routePatterns.platformBillingRun,
      operation: 'platform.billing.due.read',
      /*
       * ── THE READ'S CAPABILITY, NOT THE ACT'S, AND THE CONTROL CAUGHT ME DECLARING THE ACT'S ──
       *
       * A first draft put `platform.billing.run.execute` here and `areas.test.ts` refused it: a
       * tab whose capability is STRONGER than its own read hides a screen the session may open.
       *
       * AFTER 2026-11-03 THAT IS NOT A TECHNICALITY, IT IS THE POINT. The Owner: *"המערכת תכין
       * טבלת המלצות לחיוב... אני אבחר לקוחות, אבדוק ואאשר במפורש."* The recommendation table is
       * for READING, under the same authority the rest of this group reads under; approving the
       * charge is `platform.billing.run.execute`, which the screen's own control asks for and
       * which fewer people hold. Declaring the act's capability here would have hidden the table
       * from everyone who may look at it and may not press.
       */
      capability: 'platform.billing.history.read',
      patterns: [routePatterns.platformBillingRun],
    },
    {
      id: 'billingDunning',
      label: 'nav.billingDunning',
      href: routePatterns.platformDunning,
      operation: 'platform.dunning.read',
      capability: 'platform.billing.history.read',
      patterns: [routePatterns.platformDunning],
    },
    {
      id: 'billingChangeRequests',
      label: 'nav.billingChangeRequests',
      href: routePatterns.platformChangeRequests,
      operation: 'platform.change_request.read',
      capability: 'platform.billing.history.read',
      patterns: [routePatterns.platformChangeRequests],
    },
  ],
};

/** קטלוג ומחירונים — the approved group, as two tabs, each behind its own capability. */
const PLATFORM_CATALOGUE: Area = {
  id: 'platformCatalogue',
  label: 'nav.catalogue',
  surface: 'PLATFORM',
  tabs: [
    {
      id: 'cataloguePlans',
      label: 'nav.cataloguePlans',
      href: routePatterns.platformCatalogue,
      operation: 'platform.catalogue.plan.read',
      capability: 'platform.subscription.plan.assign',
      patterns: [routePatterns.platformCatalogue],
    },
    {
      id: 'priceBooks',
      label: 'nav.priceBooks',
      href: routePatterns.platformPriceBooks,
      operation: 'platform.price_book.read',
      capability: 'platform.catalogue.sku.manage',
      patterns: [routePatterns.platformPriceBooks, routePatterns.platformPriceBook],
    },
  ],
};

/** אבטחה וביקורת — the audit log and the support grants that are written into it. */
const PLATFORM_SECURITY: Area = {
  id: 'platformSecurity',
  label: 'nav.security',
  surface: 'PLATFORM',
  tabs: [
    {
      id: 'platformAudit',
      label: 'nav.security',
      href: routePatterns.platformSecurity,
      operation: 'platform.audit.read',
      capability: 'platform.audit.read',
      patterns: [routePatterns.platformSecurity],
    },
    {
      id: 'platformSupportGrants',
      label: 'nav.supportAccess',
      href: routePatterns.platformSupportGrants,
      operation: 'platform.support_grant.read',
      capability: 'platform.tenant.read',
      patterns: [routePatterns.platformSupportGrants],
    },
  ],
};

/**
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *  חיבורים ותמיכה — AN AREA OF ITS OWN SINCE 2026-10-28, and it had to become one.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Owner, looking at the settings screen: *"עוד טאבים שלא קשורים להגדרות אלא לחיבורים ותמיכה."*
 *
 * He is right, and the rail already said so: `connectionsAndSupport` is its own group there, beside
 * `office` and not inside it. The connection to an accounting system is not a setting of the
 * organisation — it is a relationship with another product, with its own lifecycle, its own
 * failures and its own screen. It was a tab in ORGANISATION only because that area was the only one
 * a tenant had, which is an accident of order and not a decision about what belongs together.
 *
 * ONE TAB TODAY, and that is honest rather than embarrassing: the support-access screen shares an
 * address with the organisation's audit screen (`tenantSecurity`), and `areas.test.ts` forbids one
 * address from being in two tabs. So this area names what it actually owns, and gains the support
 * surface when that surface has an address of its own.
 */
const CONNECTIONS_AND_SUPPORT: Area = {
  id: 'connectionsAndSupport',
  label: 'nav.connectionsAndSupport',
  surface: 'TENANT',
  tabs: [
    {
      id: 'connectionsSources',
      label: 'nav.connections',
      href: routePatterns.tenantSources,
      operation: 'tenant.source.list',
      capability: 'tenant.settings.read',
      patterns: [routePatterns.tenantSources, routePatterns.tenantSourceConnection],
    },
  ],
};

export const AREAS: readonly Area[] = [
  PLATFORM_BILLING_CENTRE,
  ROLES_AND_PERMISSIONS,
  COMPANIES,
  CONNECTIONS_AND_SUPPORT,
  PLATFORM_CUSTOMERS,
  PLATFORM_CATALOGUE,
  PLATFORM_SECURITY,
];

/**
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *  THE ADDRESSES THAT ARE IN NO AREA, AND WHY EACH ONE IS NOT.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * A strip of ONE tab is not a strip — it is a label pretending to be navigation — so a screen with
 * no sub-topics has none, and the bar then carries חזרה alone, in the same place and at the same
 * height as everywhere else. The Owner asked for a fixed position, not for furniture on a screen
 * that has nothing to put in it.
 */
export const NO_AREA: Readonly<Record<string, string>> = {
  /*
   * ── THE FOUR BILLING SCREENS LEFT THIS MAP ON 2026-11-03 ──────────────────────────────────
   *
   * They were all declared area-less here under this map's own rule -- one tab is not a strip --
   * which was correct while מרכז חיוב was ONE screen. It is four now, and the Owner asked for the
   * group: *"הפוך את „מרכז חיוב" בסרגל הימני לקבוצה נפתחת."* They are `PLATFORM_BILLING_CENTRE`
   * above, and the entry is removed here rather than left to contradict it -- a route in both
   * places is two answers to *"what is this screen part of"*.
   */
  /* Before a session exists there is no surface, no rail and nothing to be an area of. */
  [routePatterns.signIn]: 'pre-session — no session, no surface, no area',
  [routePatterns.welcome]: 'the public page — read before anybody has an account',
  [routePatterns.invitationAccept]: 'pre-session — reached from a mail link',
  [routePatterns.passwordReset]: 'pre-session — reached from a mail link',
  [routePatterns.emailChangeConfirm]: 'pre-session — reached from a mail link',
  /* 0032. The public door. The first is reached by somebody with no account at all, and the
     second from a mail link — so neither has a surface to be an area of. */
  [routePatterns.publicSignup]: 'pre-session — no account exists yet, so no surface and no area',
  [routePatterns.signupVerify]: 'pre-session — reached from a mail link',
  /*
   * 0041. NO AREA, AND IT IS A DECISION RATHER THAN AN OMISSION.
   *
   * It is reached by a REDIRECT FROM ANOTHER COMPANY'S SERVER, and by two different kinds of
   * person: a registering stranger with no account, and a signed-in office replacing its card.
   * Placing it under the billing area would draw the office's rail around a screen a stranger
   * also lands on; placing it under the public door would draw nothing for the office. So it
   * draws the pre-session chrome for both, which is what it has in common with the mailed
   * landings above it, and the screen's own single link is the way back.
   */
  [routePatterns.paymentReturn]:
    'pre-session — reached by a redirect from the clearing provider, by a stranger or by an office',
  [routePatterns.mfaEnrolment]: 'pre-session — the door, not a place inside the product',
  [routePatterns.mfaChallenge]: 'pre-session — the door, not a place inside the product',

  /* The top of each tree. The rail IS the navigation here; there is nothing above to go back to. */
  '/': 'the landing decision — routes onwards by surface',
  [routePatterns.tenantHome]: 'the top of the customer tree',
  /*
   * THE SETTINGS LANDING, area-less since 2026-10-28 and deliberately so. It is a MENU of the
   * area's categories; a tab bar above a menu of the same categories is the duplication the Owner
   * named. The bar returns one level down, on each category, where its tabs are that category's
   * own topics.
   */
  [routePatterns.tenantAdmin]: 'the settings landing -- a grid of categories, which is itself the menu',
  /*
   * THE ONE-SCREEN SETTINGS CATEGORIES, 2026-10-28. Each is a card on that landing and a screen of
   * its own; a strip listing the other categories from inside one of them is the duplication the
   * Owner named. `AreaBar` still draws חזרה at its fixed place, and חזרה goes to הגדרות.
   */
  [routePatterns.tenantOrganisation]: 'a settings category of its own -- הארגון שלי',
  [routePatterns.tenantUsers]: 'a settings category of its own -- משתמשים',
  /*
   * §6. THE OFFICE'S OWN WORK, and not a settings category: a person opens it to do the day's
   * work rather than to configure anything. It is its own top-level destination for the same
   * reason the companies list is.
   */
  [routePatterns.tenantWorkItems]: 'a destination of its own -- the office\u2019s work, not a setting',
  /*
   * 0030/0031. A SETTINGS CATEGORY and not a destination: a person opens it to configure what
   * the scheduler raises, which is the opposite of the work list above it.
   */
  [routePatterns.tenantReminderRules]: 'a settings category of its own -- reporting deadlines',
  [routePatterns.tenantPersonalisation]:
    'a settings category of its own -- the home screen’s cards, per person',
  [routePatterns.tenantUserInvitation]: 'the users screen with the invitation form open',
  [routePatterns.tenantUser]: 'one person, beneath the users category',
  [routePatterns.tenantSecurity]: 'a settings category of its own -- אבטחה וביקורת',
  [routePatterns.tenantSubscription]: 'a settings category of its own -- מנוי וחשבוניות',
  /*
   * §11/§12. AREA-LESS FOR THE SAME REASON AS THE SUBSCRIPTION ABOVE IT: a settings category
   * of its own, with its sub-topics as TABS on the screen rather than as addresses under it.
   * `TabStrip` puts the selected tab in the fragment, so support can still link to a half.
   */
  [routePatterns.tenantBilling]: 'a settings category of its own -- חשבוניות, חיובים וביטול',
  [routePatterns.platformConsole]: 'the top of the platform tree',

  /*
   * THE COMPANY WORKSPACE. A company opens its own modules, and not one of the eleven exists yet
   * (`FINANCIAL_MODULES`, every one `planned`). Tabs here would be a strip of unbuilt places; the
   * bar carries חזרה, which is the one thing these screens genuinely need.
   */
  [routePatterns.companies]: 'the company picker — a list, not an area',
  [routePatterns.companyHome]: 'one company — its modules are not built yet',
  [routePatterns.moduleHome]: 'inside one company, beneath its own home',
  [routePatterns.parentScreen]: 'inside one module, beneath its own home',
  [routePatterns.blockedDocument]: 'a refusal about one document',

  /* Single screens opened from the platform rail, each with no sub-topic beneath it. */
  [routePatterns.platformCommercialPolicy]: 'one settings screen — no sub-topics',
  [routePatterns.platformSignupDoor]: 'one settings screen — no sub-topics',
  [routePatterns.platformHealth]: 'one status screen — no sub-topics',

  /* The signed-in person's own account. It belongs to the person, not to an area of the product. */
  [routePatterns.personalDetails]:
    'the account that is signed in, on any surface — it belongs to the person and not to an office',
  [routePatterns.personalSecurity]: 'the account that is signed in, on any surface',
};

/** The tabs of an area this session may see, in declared order. */
export function visibleTabs(area: Area, capabilities: readonly string[]): readonly AreaTab[] {
  return area.tabs.filter((tab) => tab.capability === null || capabilities.includes(tab.capability));
}

/**
 * WHICH AREA AN ADDRESS IS IN, and which of its tabs is the current one.
 *
 * `match` is handed in rather than imported so this module stays free of the router: it is read by
 * the shell, which is mounted by the suites and by the harness without one. `AreaBar` passes
 * `matchPath`, which is the router's own answer about the pattern it was given — the same function
 * `App.tsx` already uses to decide whether an address is pre-session.
 */
export function areaForPath(
  pathname: string,
  match: (pattern: string, pathname: string) => boolean,
): { readonly area: Area; readonly currentTabId: string | null } | null {
  for (const area of AREAS) {
    for (const tab of area.tabs) {
      if (tab.patterns.some((pattern) => match(pattern, pathname))) {
        return { area, currentTabId: tab.id };
      }
    }
  }
  return null;
}
